import { ErrorBoundary } from '@dynamic/ui';
import { FC, Suspense, useMemo } from 'react';
import { BrowserRouter, useLocation } from 'react-router';
import styled from 'styled-components';

import { useLoginTveComplete } from '#/components/Login/LoginTVE.hook';
import { ServiceSelector } from '#/dev/ServiceSelector';
import { GeoRestrictionError } from '#/errors/serviceErrors';
import { useUnauthenticatedHomePage } from '#/pages/UnauthenticatedHomePage/UnauthenticatedHomePage.hook';
import { useUpdateHistory } from '#/pages/WatchHistory/useUpdateHistory';
import { AdsManager } from '#/plugins/ads/AdsManager';
import { AnalyticsManager } from '#/plugins/analytics/AnalyticsManager';
import { PageAdsManager } from '#/plugins/pageAds/PageAdsManager';
import { Paywall } from '#/plugins/paywall/Paywall';
import { PaywallManager } from '#/plugins/paywall/PaywallManager';
import { PrivacyManager } from '#/plugins/privacy/PrivacyManager';
import { AppStatus, StorageKeys } from '#/service/constants';
import { useTranslationStore } from '#/store/translation/translation.store';

import { initApp } from './appInit';
import { AppRoutes } from './AppRoutes';
import { Footer } from './components/Footer/Footer';
import { Header } from './components/Header/Header';
import { TOP_MENU_HEIGHT_PX } from './components/Header/Menu.utils';
import { Login } from './components/Login/Login';
import { GenericModal } from './components/Modal/GenericModal';
import { PageBackground } from './components/PageBackground/PageBackground';
import { PreferencesPrompt } from './components/PreferencesPrompt/PreferencesPrompt';
import { ScrollToTop } from './components/ScrollToTop';
import { Snackbar } from './components/Snackbar/Snackbar';
import { Splash } from './components/Splash/Splash';
import { Fallback } from './components/Status/Fallback';
import { TermsModal } from './components/Terms/TermsModal';
import { useInitAutoLogin } from './hooks/useAutoLogin';
import { useConfig } from './hooks/useConfig';
import { useMenu } from './hooks/useMenu';
import { useAuthStore } from './store/auth/auth.store';
import { useBootStore } from './store/boot/boot.store';

const PageContainer = styled.div`
  /* pages can use this as a top margin to prevent content sliding under the menu */
  --menuHeight: calc(${TOP_MENU_HEIGHT_PX}px + var(--bannerHeight, 0px));

  min-height: 100vh;
`;

initApp();

const devAdsEnabled = localStorage.getItem(StorageKeys.DEVTOOLS_ADS) === 'true';

const shouldStartSplashFadeout = (
  isBooted: boolean,
  isDictionaryReady: boolean,
  isPluginManagerReady: boolean,
  isGeoBlocked: boolean
) => isBooted && isDictionaryReady && (isPluginManagerReady || isGeoBlocked);

const checkAppLoadingState = (
  isBooted: boolean,
  isDictionaryReady: boolean,
  isGeoBlocked: boolean
) => {
  if (isBooted && !isDictionaryReady && !isGeoBlocked) {
    return AppStatus.Loading;
  }

  if (isBooted && isDictionaryReady && !isGeoBlocked) {
    return AppStatus.Active;
  }
};

const App: FC = () => {
  const location = useLocation();
  const { isLoggedIn, isEntitled } = useAuthStore();

  useLoginTveComplete();
  useTranslationStore();

  const { restrictions: { geoblocked: isGeoBlocked = false } = {} } =
    useConfig();

  const { shouldShow: shouldShowUnauthenticatedHomePage } =
    useUnauthenticatedHomePage();

  const menu = useMenu();

  const emptyMenu = useMemo(
    () => ({ ...menu, mainMenuItems: [], linkMenuItems: [] }),
    [menu]
  );
  const { isBooted, isDictionaryReady, isPluginManagerReady } = useBootStore();

  // We have to re-render the error boundary to dismiss the error. Otherwise, if you run into a "login required"
  // error, it won't disappear even after you logged in. Errors should also be dismissed when navigating back (or away).
  // Add more terms into the key as needed.
  const errorBoundaryRerenderKey = [
    'error',
    isLoggedIn,
    isEntitled,
    location.pathname,
  ].join(',');

  const startSplashFadeout = shouldStartSplashFadeout(
    isBooted,
    isDictionaryReady,
    isPluginManagerReady,
    isGeoBlocked
  );

  const appLoadingState = checkAppLoadingState(
    isBooted,
    isDictionaryReady,
    isGeoBlocked
  );

  useInitAutoLogin();
  useUpdateHistory();

  return (
    <>
      <ScrollToTop />
      {isGeoBlocked && <Fallback error={new GeoRestrictionError()} />}
      {appLoadingState === AppStatus.Active && (
        <>
          {/* Main error boundary for the rest of the app */}
          <ErrorBoundary
            FallbackComponent={Fallback}
            rerenderKey={errorBoundaryRerenderKey}
          >
            <>
              <PageContainer>
                <Header
                  cacheKey="app-header"
                  menu={startSplashFadeout ? menu : emptyMenu}
                  shouldShowUnauthenticatedHomePage={
                    shouldShowUnauthenticatedHomePage
                  }
                />
                <Suspense>
                  <AppRoutes />
                </Suspense>
              </PageContainer>
              <Footer />
            </>
          </ErrorBoundary>
          {/* Modals are in a separate boundary, so even if the rest of the app fails and renders Fallback, they stay in the DOM. */}
          <ErrorBoundary FallbackComponent={Fallback}>
            <>
              <Login />
              <TermsModal isAppReady={isBooted && isDictionaryReady} />
              <GenericModal />
              <PreferencesPrompt />
            </>
          </ErrorBoundary>
        </>
      )}
      <AnalyticsManager />
      <PaywallManager />
      <Paywall />
      {(process.env.goal !== 'local' || devAdsEnabled) && (
        <>
          <AdsManager />
          <PageAdsManager />
        </>
      )}
      <PrivacyManager />
      <Splash menu={menu} startFadeout={startSplashFadeout} />
      {process.env.goal === 'local' && <ServiceSelector />}
      <Snackbar />
    </>
  );
};

// we need this separated from App, so hooks and components used in App can access router stuff, like useLocation
export const AppRouter = () => (
  <BrowserRouter>
    <PageBackground />
    <App />
  </BrowserRouter>
);
